// 管理後台主頁（可擴充左側選單與內容區） - Material UI 版本
import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Link,
  Snackbar
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  ShoppingCart,
  TableChart,
  Image,
  Login,
  Notifications,
  Apps as AppsIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationCity as LocationCityIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { Outlet, useNavigate } from 'react-router-dom';

const drawerWidth = 200;
const collapsedDrawerWidth = 60;

export default function AdminLayout() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // 更精確的手機判斷：針對特殊手機尺寸 407×904 (3x) + Material UI breakpoint
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || (
    typeof window !== 'undefined' && 
    ((window.screen.width <= 500) || (windowSize.width <= 1000 && window.devicePixelRatio >= 2))
  );

  // 檢查登入狀態（驗證後端 session）
  const checkAuthStatus = async () => {
    try {
      const res = await fetch('/api/auth/status', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          return data.user;
        }
      }
      localStorage.removeItem('user');
      return null;
    } catch (e) {
      localStorage.removeItem('user');
      return null;
    }
  };

  // 監聽視窗大小變化
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    // 初始設定
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 初始載入時檢查登入狀態和權限
  useEffect(() => {
    const initAuth = async () => {
      const authUser = await checkAuthStatus();
      setUser(authUser);
      setLoading(false);
      
      if (!authUser) {
        setSnackbar({ open: true, message: '請先登入', severity: 'error' });
        navigate('/login');
        return;
      }
      
      const userRole = authUser.role?.name || authUser.role;
      const hasAdminAccess = ['最高權限', '管理者', '簽到人員'].includes(userRole);
      
      if (!hasAdminAccess) {
        setSnackbar({ open: true, message: '無權訪問後台', severity: 'error' });
        navigate('/');
      }
    };
    
    initAuth();
  }, [navigate]);

  // 角色權限定義
  const userRole = user?.role?.name || user?.role;
  const isSuperAdmin = userRole === '最高權限';
  const isAdmin = ['最高權限', '管理者'].includes(userRole);
  const isCheckinStaff = userRole === '簽到人員';
  const hasAdminAccess = ['最高權限', '管理者', '簽到人員'].includes(userRole);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAdminAccess) {
    return null; // 避免閃爍，等待重定向
  }

  // 根據角色組裝動態選單項目
  const getMenuItems = () => {
    // 簽到人員只能看到指定功能
    if (isCheckinStaff) {
      return [
        { key: 'products', icon: <AppsIcon />, label: '活動管理', onClick: () => navigate('/admin/products') },
        { key: 'checkinLogs', icon: <TableChart />, label: '簽到紀錄管理', onClick: () => navigate('/admin/checkin-logs') },
      ];
    }

    // 管理者和最高權限的完整選單
    const adminItems = [
      { key: 'products', icon: <AppsIcon />, label: '活動管理', onClick: () => navigate('/admin/products') },
      { key: 'checkinLogs', icon: <TableChart />, label: '簽到紀錄管理', onClick: () => navigate('/admin/checkin-logs') },
      { key: 'users', icon: <People />, label: '聽眾管理', onClick: () => navigate('/admin/users') },
      { key: 'orders', icon: <ShoppingCart />, label: '報名管理', onClick: () => navigate('/admin/orders') },
      { key: 'loginlog', icon: <Login />, label: '登入記錄', onClick: () => navigate('/admin/loginlog') },
      { key: 'notifications', icon: <Notifications />, label: '訊息管理', onClick: () => navigate('/admin/notifications') },
      { key: 'basic-data-community', icon: <BusinessIcon />, label: '社團管理', onClick: () => navigate('/admin/basic-data?tab=community') },
      { key: 'basic-data-department', icon: <LocationCityIcon />, label: '社別/會別管理', onClick: () => navigate('/admin/basic-data?tab=department') },
      { key: 'basic-data-position', icon: <WorkIcon />, label: '社職/會職管理', onClick: () => navigate('/admin/basic-data?tab=position') },
      { key: 'wheel', icon: <Image />, label: '幸運大轉盤', onClick: () => window.open('https://wheelofnames.com/zh-HK/', '_blank') },
    ];

    // 角色管理僅最高權限可見
    if (isSuperAdmin) {
      adminItems.push({ key: 'roles', icon: <PersonIcon />, label: '角色管理', onClick: () => navigate('/admin/roles') });
    }

    return adminItems;
  };

  const menuItems = getMenuItems();

  // 取得螢幕解析度資訊
  const getScreenInfo = () => {
    if (typeof window === 'undefined') return '';
    
    const width = window.screen.width;
    const height = window.screen.height;
    const devicePixelRatio = window.devicePixelRatio || 1;
    const viewportWidth = windowSize.width || window.innerWidth;
    const viewportHeight = windowSize.height || window.innerHeight;
    
    return `${width}×${height} (${devicePixelRatio}x) | 視窗:${viewportWidth}×${viewportHeight} ${isMobile ? '📱' : '💻'}`;
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 側邊欄內容
  const drawerContent = (
    <Box>
      <Toolbar />
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              onClick={item.onClick}
              sx={{
                minHeight: 48,
                justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: sidebarCollapsed && !isMobile ? 'auto' : 3,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                sx={{ opacity: sidebarCollapsed && !isMobile ? 0 : 1 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { 
            md: isMobile ? '100%' : `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` 
          },
          ml: { 
            md: isMobile ? 0 : `${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px` 
          },
          bgcolor: '#111'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {isMobile ? '管理後台' : '大型活動 管理後台'}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#ccc', 
                opacity: 0.8,
                fontSize: isMobile ? 10 : 12
              }}
            >
              {getScreenInfo()}
            </Typography>
            
            <Link
              href="/"
              color="inherit"
              underline="none"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? 14 : 20,
                letterSpacing: 1
              }}
            >
              前台連結
            </Link>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 側邊欄 */}
      <Box
        component="nav"
        sx={{ 
          width: { md: isMobile ? 0 : (sidebarCollapsed ? collapsedDrawerWidth : drawerWidth) }, 
          flexShrink: { md: 0 } 
        }}
      >
        {/* 手機版抽屜 */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        
        {/* 桌面版側邊欄 */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* 主要內容區 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: isMobile ? 1.5 : 3,
          width: {
            md: `calc(100% - ${isMobile ? 0 : (sidebarCollapsed ? collapsedDrawerWidth : drawerWidth)}px)`
          },
        }}
      >
        <Toolbar />
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minHeight: 360,
            p: 2
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
